// A component property can bind to live data from a data source component by
// holding a reference string instead of a literal value:
//
//   datasource://<data source uuid>/<json field>/<json field>/...
//
// `datasource://` is the keyword that marks the value as a binding. It is
// followed by the uuid of the data source component whose fetched payload should
// be read, then a `/`-separated path that navigates into that JSON payload
// (each segment steps one level into a nested object, or indexes into an array).
// The value found at the end of the path is substituted into the property at
// render time. See UiSchemeRenderer for where bindings are resolved.

export const DATA_SOURCE_REFERENCE_PREFIX = "datasource://";

export interface DataSourceReference {
  uuid: string;
  path: string[];
}

// Quick predicate used before the (slightly) more expensive parse.
export function isDataSourceReference(value: unknown): value is string {
  return typeof value === "string"
    && value.trimStart().startsWith(DATA_SOURCE_REFERENCE_PREFIX);
}

// Parse `datasource://<uuid>/<field>/<field>` into its uuid and navigation path.
// Returns null for anything that is not a data source reference. Path segments
// are URI-decoded so fields containing reserved characters can be encoded.
export function parseDataSourceReference(
  value: unknown,
): DataSourceReference | null {
  if (typeof value !== "string") return null;

  const trimmed = value.trim();
  if (!trimmed.startsWith(DATA_SOURCE_REFERENCE_PREFIX)) return null;

  const remainder = trimmed.slice(DATA_SOURCE_REFERENCE_PREFIX.length);
  const [rawUuid, ...rawPath] = remainder.split("/");
  const uuid = decodeSegment(rawUuid ?? "");
  // The uuid must be present; empty path segments (e.g. trailing or doubled
  // slashes) are dropped so navigation stays well-formed.
  if (!uuid) return null;

  const path = rawPath
    .map(decodeSegment)
    .filter((segment) => segment.length > 0);

  return { uuid, path };
}

// Walk a JSON value along a path of object keys / array indices. Any missing
// key, out-of-range index, or attempt to descend into a non-object returns
// undefined so the consuming property simply falls back to its default.
export function resolveJsonPath(data: unknown, path: readonly string[]): unknown {
  let current = data;
  for (const segment of path) {
    if (current == null) return undefined;
    if (Array.isArray(current)) {
      const index = Number(segment);
      if (!Number.isInteger(index) || index < 0 || index >= current.length) {
        return undefined;
      }
      current = current[index];
    } else if (typeof current === "object") {
      current = (current as Record<string, unknown>)[segment];
    } else {
      return undefined;
    }
  }
  return current;
}

function decodeSegment(segment: string): string {
  try {
    return decodeURIComponent(segment);
  } catch {
    // Leave malformed percent-encoding untouched rather than throwing.
    return segment;
  }
}
